import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';

type TracingClient = Parameters<
  ReturnType<typeof Sentry.browserTracingIntegration>['afterAllSetup']
>[0];

// @sentry/react 8.36 tanstackRouterBrowserTracingIntegration reads
// fromLocation.state in onBeforeNavigate, and TanStack passes no fromLocation
// on the first navigation, so it throws.
export const tanstackRouterTracingIntegration = (router: AnyRouter) => {
  const browserTracing = Sentry.browserTracingIntegration({
    instrumentNavigation: false,
    instrumentPageLoad: false,
  });

  const routeSpanName = (
    pathname: string,
  ): { name: string; source: 'route' | 'url' } => {
    const match = router.matchRoutes(pathname, {}).at(-1);
    return match
      ? { name: match.routeId, source: 'route' }
      : { name: pathname, source: 'url' };
  };

  const spanOptions = (op: string, pathname: string) => {
    const { name, source } = routeSpanName(pathname);
    return {
      name,
      attributes: {
        'sentry.op': op,
        'sentry.origin': `auto.${op}.react.tanstack_router`,
        'sentry.source': source,
      },
    };
  };

  return {
    ...browserTracing,
    afterAllSetup(client: TracingClient) {
      browserTracing.afterAllSetup(client);
      Sentry.startBrowserTracingPageLoadSpan(
        client,
        spanOptions('pageload', router.state.location.pathname),
      );
      router.subscribe(
        'onBeforeNavigate',
        ({ fromLocation, toLocation, hrefChanged }) => {
          if (!fromLocation || !hrefChanged) return;
          const span = Sentry.startBrowserTracingNavigationSpan(
            client,
            spanOptions('navigation', toLocation.pathname),
          );
          const unsubscribe = router.subscribe('onResolved', (resolved) => {
            unsubscribe();
            const { name, source } = routeSpanName(
              resolved.toLocation.pathname,
            );
            span?.updateName(name);
            span?.setAttribute('sentry.source', source);
            Sentry.getCurrentScope().setTransactionName(name);
          });
        },
      );
    },
  };
};
